/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { ISystemPanelFrameFacade } from '../../../../../data-access/src/lib/domain/facades-models/system-panel-frame-facade-interface';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import {
  selectSystemInfo,
  selectDockablePanelList,
  selectDockableProjectList,
  selectHangerNeeded,
  selectIsClearance,
} from '../tool-belt.selector';
import { Observable, from } from 'rxjs';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import {
  initializeProjectList,
  sendPopupRoutingAction,
  updateIsClearance,
  InitializeSystemInfo,
  GetConfigurationLocation,
} from '../toolbelt.actions';
import { LocalStorageService } from '../../../../../../toolbelt/utils/src/lib/local-storage/local-storage.service';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { SystemStateManager } from '../../../../../utils/src/lib/system-state-manager.service';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { TooltipItem } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tooltip-item';
import { TooltipItemInfo } from '../../../../../utils/src/lib/common-helper/tooltip-item-info/tooltip-item-info';
import { PanelDetails } from '../../models/panel-detail';
import { BooleanObject } from '../../models/boolean-object';
import { BackendService } from '../../../../../utils/src/lib/sysque-backend/backend.service';
import { DataInitializerService } from '../../../../../utils/src/lib/sysque-backend/data-initializer.service';
import { DownloadFamiliesReturnCodes } from '../../models/download-families-return-codes';
import { GlobalConstants } from '../../constants/global-constants';
import { SelectedPanelDataItem } from '../../models/selected-panel-data-item';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';

@Injectable({ providedIn: 'root' })
export class SystemPanelFrameFacade implements ISystemPanelFrameFacade {
  constructor(
    private store: Store,
    private localStorageService: LocalStorageService,
    private stateManager: SystemStateManager,
    private backendService: BackendService,
    private dataInitializerService: DataInitializerService
  ) {}

  public getSysQueConnectOnState(): boolean {
    return this.getLocalStorage(GlobalConstants.IS_SYSQUE_CONNECT_ON);
  }

  public getWireFillList(projectName: string, categoryName: string): Promise<string[]> {
    return this.dataInitializerService.initializeWireList(projectName, categoryName);
  }

  public getConfigurationLocation(): void {
    this.store.dispatch(GetConfigurationLocation());
  }

  public selectProjectList(): Observable<string[]> {
    return this.store.select(selectDockableProjectList);
  }

  public getLocalStorage(key: string): any {
    return this.localStorageService.getStorage(key);
  }

  public getSystems(): Observable<SystemInfo[]> {
    return from<Promise<SystemInfo[]>>(this.backendService.messageHandler.sendRequestAsync('GetSystems'));
  }

  public getProjectList(): void {
    this.store.dispatch(initializeProjectList());
  }

  public selectDockablePanelList(): Observable<PanelDetails[]> {
    return this.store.select(selectDockablePanelList);
  }

  public notifyGetDockablePanelListAction(): Promise<PanelDetails[]> {
    return this.dataInitializerService.getDockPanelList();
  }

  public getPanelType(systemName: string): Promise<boolean> {
    return this.dataInitializerService.checkPanelSystem(systemName);
  }

  public getCategories(systemName: string): Promise<TabDescription[]> {
    return this.dataInitializerService.initializeCategoryList(systemName);
  }

  public getCircuitList(projectName: string, panelName: string): Promise<string[]> {
    return this.dataInitializerService.getCircuitList(projectName, panelName);
  }

  public openHangerSpecification(): void {
    let sysquePopupConfig = new SysquePopupConfig();
     createDialog(sysquePopupConfig, GlobalConstants.HANGER_SPECIFICATION_FAMILY_INFORMATION_DIALOG);
     this.store.dispatch(
       sendPopupRoutingAction({
         route: GlobalConstants.HANGER_SPECIFICATION_FAMILY_INFORMATION_DIALOG,
         width: sysquePopupConfig.width,
         height: sysquePopupConfig.height,
       })
     );
   }

  public updateHangerNeeded(systemName: string, isChecked: boolean): Promise<void> {
    return this.backendService.messageHandler.sendRequestAsync(GlobalConstants.UpdateIsHangerNeeded, [systemName, isChecked]);
  }

  public selectHangerNeeded(): Observable<BooleanObject> {
    return this.store.select(selectHangerNeeded);
  }

  public updateClearance(systemName: string, isChecked: boolean): void {
    this.store.dispatch(updateIsClearance({ systemName, isChecked }));
  }

  public selectClearance(): Observable<BooleanObject> {
    return this.store.select(selectIsClearance);
  }

  public getAddFittingState(): SysqueElectricalState {
    return this.stateManager.getAddFittingModeState();
  }

  public getDockedState(): SysqueElectricalState {
    return this.stateManager.getDockedModeState();
  }

  public getFittingsList(system: string, category: string, isSysQueConnect: boolean): Promise<Item[]>{
    return this.dataInitializerService.initializeFittingsList(system, category, isSysQueConnect)
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public sendPopupRoutingAction(route: string, width: number, height: number): void {
    this.store.dispatch(sendPopupRoutingAction({ route, width: width, height: height }));
  }

  public selectSystems(): Observable<SystemInfo[]> {
    const systemList$ = this.store.select(selectSystemInfo);
    systemList$.subscribe((data) => {
      const state = this.stateManager.getDockedModeState();
      state.systemList = data;
    });
    return systemList$;
  }

  public getTooltipItemInfo(item: Item, category: string): TooltipItem[] {
    return new TooltipItemInfo().getTooltipItemInfo(item, category);
  }

  public getHangerSetUpStatus(): boolean {
    return this.localStorageService.getStorage(GlobalConstants.HangerCopySetUp);
 }

  public getFittings(systemName: string, categoryName: string): Promise<Item[]> {
    return this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemNameAndCategory, [systemName, categoryName, false]);
  }

  public downloadUnreferencedFamilies(systemName: string): Observable<boolean> {
    return this.backendService.downloadUnreferencedFamilies(systemName);
  }

  public checkIfUnreferencedFamilies(systemName: string): Promise<DownloadFamiliesReturnCodes> {
    return this.backendService.checkIfUnreferencedFamilies(systemName);
  }

  public updateSystemList(): void {
    this.store.dispatch(InitializeSystemInfo());
  }

  public applyPanelDataLogic(panelData: SelectedPanelDataItem, isWireFillSpec: boolean): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.ApplyPanelDataLogic, [
      panelData,
      isWireFillSpec,
    ]);
  }

  public executePasteItemsAction(replaceItems: PastedItemsInfo[]): Promise<void> {
    return this.backendService.messageHandler.sendRequestAsync(GlobalConstants.LogicToExecuteForPasteItems, [replaceItems]);
  }

  public checkForDuplicateItems(items: PastedItemsInfo[]): Observable<PastedItemsInfo[]> {
    return from<Promise<PastedItemsInfo[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.CheckForDuplicateItems, [items])
    );
  }

  public checkIfCurrentWindowIsDockable(): boolean {
    return this.localStorageService.getStorage(GlobalConstants.DockableKey);
  }
}
